/**
 * Indicator math + the runtime model used by the chart.
 *
 * Indicators are computed client-side from the bars the chart is already
 * showing — no extra API round-trip. Each indicator declares which "pane" it
 * lives in (price = overlay on the candles, below = its own pane underneath).
 *
 * Param values can be `$paramName` references that the chart resolves against
 * the backtest's strategy_params before computing.
 */

export type IndicatorType =
  | "sma"
  | "ema"
  | "wma"
  | "bollinger"
  | "donchian"
  | "rsi"
  | "macd"
  | "stochastic"
  | "atr"
  | "roc"
  | "supertrend"
  | "vwap";
export type IndicatorPane = "price" | "below";

export interface IndicatorSpec {
  type: IndicatorType;
  params: Record<string, number | string>;
  pane: IndicatorPane;
  color?: string;
  label?: string;
}

/** A single rendered line on a chart pane. */
export interface IndicatorSeries {
  label: string;
  color: string;
  pane: IndicatorPane;
  /** time = seconds since UTC epoch (lightweight-charts UTCTimestamp). */
  data: { time: number; value: number }[];
}

export interface IndicatorInstance {
  /** Stable id so the chart can replace/remove. */
  id: string;
  type: IndicatorType;
  params: Record<string, number>;
  pane: IndicatorPane;
  /** Display name used in the indicators panel and series legend. */
  label: string;
  /** Optional explicit color; if omitted, we pick from a palette. */
  color?: string;
  /** Line width in CSS pixels. Defaults to 1 if omitted. */
  lineWidth?: number;
}

/** Full OHLCV bar — needed by Stochastic, ATR, Donchian, etc. */
export interface OhlcvBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

const PALETTE = [
  "#0ea5e9", // sky
  "#f97316", // orange
  "#a855f7", // violet
  "#14b8a6", // teal
  "#eab308", // yellow
  "#ec4899", // pink
];

let nextIdSeq = 1;

export function newIndicatorId(): string {
  return `ind-${nextIdSeq++}`;
}

export function colorForIndex(i: number): string {
  return PALETTE[i % PALETTE.length];
}

/**
 * Replace `$paramName` strings inside a spec's params with values from the
 * strategy_params object. Numbers pass through untouched. Unresolvable refs
 * fall back to NaN — the caller should drop those before computing.
 */
export function resolveSpec(
  spec: IndicatorSpec,
  strategyParams: Record<string, unknown>,
): {
  type: IndicatorType;
  params: Record<string, number>;
  pane: IndicatorPane;
  color?: string;
  label?: string;
} {
  const resolved: Record<string, number> = {};
  for (const [k, v] of Object.entries(spec.params)) {
    if (typeof v === "number") {
      resolved[k] = v;
    } else if (typeof v === "string" && v.startsWith("$")) {
      const ref = v.slice(1);
      const refVal = strategyParams[ref];
      const n = Number(refVal);
      resolved[k] = Number.isFinite(n) ? n : NaN;
    } else {
      const n = Number(v);
      resolved[k] = Number.isFinite(n) ? n : NaN;
    }
  }
  return {
    type: spec.type,
    params: resolved,
    pane: spec.pane,
    color: spec.color,
    label: spec.label,
  };
}

export function defaultLabelFor(
  type: IndicatorType,
  params: Record<string, number>,
): string {
  switch (type) {
    case "sma":
      return `SMA(${params.period})`;
    case "ema":
      return `EMA(${params.period})`;
    case "wma":
      return `WMA(${params.period})`;
    case "bollinger":
      return `BB(${params.period},${params.std_dev})`;
    case "donchian":
      return `Donchian(${params.period})`;
    case "rsi":
      return `RSI(${params.period})`;
    case "macd":
      return `MACD(${params.fast_period},${params.slow_period},${params.signal_period})`;
    case "stochastic":
      return `Stoch(${params.k_period},${params.d_period})`;
    case "atr":
      return `ATR(${params.period})`;
    case "roc":
      return `ROC(${params.period})`;
    case "supertrend":
      return `ST(${params.atr_period},${params.multiplier})`;
    case "vwap":
      return "VWAP";
  }
}

/**
 * Compute all line series for an indicator instance against the given bars.
 * Multi-line indicators (Bollinger, Donchian, MACD, Stochastic) return >1.
 */
export function computeIndicator(
  instance: IndicatorInstance,
  bars: OhlcvBar[],
): IndicatorSeries[] {
  switch (instance.type) {
    case "sma":
      return [wrap(instance, sma(bars, instance.params.period), instance.label)];
    case "ema":
      return [wrap(instance, ema(bars, instance.params.period), instance.label)];
    case "wma":
      return [wrap(instance, wma(bars, instance.params.period), instance.label)];
    case "bollinger": {
      const { upper, middle, lower } = bollinger(
        bars,
        instance.params.period,
        instance.params.std_dev,
      );
      const base = instance.color ?? "#0ea5e9";
      return [
        wrap(instance, upper, `${instance.label} ↑`, base),
        wrap(instance, middle, `${instance.label} mid`, base),
        wrap(instance, lower, `${instance.label} ↓`, base),
      ];
    }
    case "donchian": {
      const { upper, middle, lower } = donchian(bars, instance.params.period);
      const base = instance.color ?? "#a855f7";
      return [
        wrap(instance, upper, `${instance.label} ↑`, base),
        wrap(instance, middle, `${instance.label} mid`, base),
        wrap(instance, lower, `${instance.label} ↓`, base),
      ];
    }
    case "rsi":
      return [wrap(instance, rsi(bars, instance.params.period), instance.label)];
    case "macd": {
      const { line, signal } = macd(
        bars,
        instance.params.fast_period,
        instance.params.slow_period,
        instance.params.signal_period,
      );
      const base = instance.color ?? "#0ea5e9";
      return [
        wrap(instance, line, `${instance.label} line`, base),
        wrap(instance, signal, `${instance.label} signal`, "#f97316"),
      ];
    }
    case "stochastic": {
      const { k, d } = stochastic(
        bars,
        instance.params.k_period,
        instance.params.d_period,
      );
      const base = instance.color ?? "#0ea5e9";
      return [
        wrap(instance, k, `${instance.label} %K`, base),
        wrap(instance, d, `${instance.label} %D`, "#f97316"),
      ];
    }
    case "atr":
      return [wrap(instance, atr(bars, instance.params.period), instance.label)];
    case "roc":
      return [wrap(instance, roc(bars, instance.params.period), instance.label)];
    case "supertrend":
    case "vwap":
      // Rendered natively by klinecharts (see lib/klineIndicators.ts); this
      // legacy client-side compute path is unused for them.
      return [];
  }
}

function wrap(
  instance: IndicatorInstance,
  data: { time: number; value: number }[],
  label: string,
  color?: string,
): IndicatorSeries {
  return {
    label,
    color: color ?? instance.color ?? "#0ea5e9",
    pane: instance.pane,
    data,
  };
}

// ---------------------------------------------------------------------------
// Math
// ---------------------------------------------------------------------------

function sma(
  bars: OhlcvBar[],
  period: number,
): { time: number; value: number }[] {
  if (!Number.isFinite(period) || period < 1) return [];
  const out: { time: number; value: number }[] = [];
  let sum = 0;
  for (let i = 0; i < bars.length; i++) {
    sum += bars[i].close;
    if (i >= period) sum -= bars[i - period].close;
    if (i >= period - 1) {
      out.push({ time: bars[i].time, value: sum / period });
    }
  }
  return out;
}

function emaSeries(
  values: { time: number; v: number }[],
  period: number,
): { time: number; value: number }[] {
  if (!Number.isFinite(period) || period < 1 || values.length < period) return [];
  const k = 2 / (period + 1);
  const out: { time: number; value: number }[] = [];
  let seed = 0;
  for (let i = 0; i < period; i++) seed += values[i].v;
  let prev = seed / period;
  out.push({ time: values[period - 1].time, value: prev });
  for (let i = period; i < values.length; i++) {
    const cur = values[i].v * k + prev * (1 - k);
    out.push({ time: values[i].time, value: cur });
    prev = cur;
  }
  return out;
}

function ema(
  bars: OhlcvBar[],
  period: number,
): { time: number; value: number }[] {
  return emaSeries(
    bars.map((b) => ({ time: b.time, v: b.close })),
    period,
  );
}

function wma(
  bars: OhlcvBar[],
  period: number,
): { time: number; value: number }[] {
  if (!Number.isFinite(period) || period < 1) return [];
  const weightSum = (period * (period + 1)) / 2;
  const out: { time: number; value: number }[] = [];
  for (let i = period - 1; i < bars.length; i++) {
    let weighted = 0;
    for (let j = 0; j < period; j++) {
      weighted += bars[i - period + 1 + j].close * (j + 1);
    }
    out.push({ time: bars[i].time, value: weighted / weightSum });
  }
  return out;
}

function bollinger(
  bars: OhlcvBar[],
  period: number,
  stdDev: number,
): {
  upper: { time: number; value: number }[];
  middle: { time: number; value: number }[];
  lower: { time: number; value: number }[];
} {
  const upper: { time: number; value: number }[] = [];
  const middle: { time: number; value: number }[] = [];
  const lower: { time: number; value: number }[] = [];
  if (!Number.isFinite(period) || period < 2) return { upper, middle, lower };
  for (let i = period - 1; i < bars.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += bars[j].close;
    const mean = sum / period;
    let varSum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      varSum += (bars[j].close - mean) ** 2;
    }
    const sd = Math.sqrt(varSum / period);
    const t = bars[i].time;
    upper.push({ time: t, value: mean + stdDev * sd });
    middle.push({ time: t, value: mean });
    lower.push({ time: t, value: mean - stdDev * sd });
  }
  return { upper, middle, lower };
}

function donchian(
  bars: OhlcvBar[],
  period: number,
): {
  upper: { time: number; value: number }[];
  middle: { time: number; value: number }[];
  lower: { time: number; value: number }[];
} {
  const upper: { time: number; value: number }[] = [];
  const middle: { time: number; value: number }[] = [];
  const lower: { time: number; value: number }[] = [];
  if (!Number.isFinite(period) || period < 1) return { upper, middle, lower };
  for (let i = period - 1; i < bars.length; i++) {
    let hi = -Infinity;
    let lo = Infinity;
    for (let j = i - period + 1; j <= i; j++) {
      if (bars[j].high > hi) hi = bars[j].high;
      if (bars[j].low < lo) lo = bars[j].low;
    }
    const t = bars[i].time;
    upper.push({ time: t, value: hi });
    middle.push({ time: t, value: (hi + lo) / 2 });
    lower.push({ time: t, value: lo });
  }
  return { upper, middle, lower };
}

function rsi(
  bars: OhlcvBar[],
  period: number,
): { time: number; value: number }[] {
  const out: { time: number; value: number }[] = [];
  if (!Number.isFinite(period) || period < 1 || bars.length < period + 1) {
    return out;
  }
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const change = bars[i].close - bars[i - 1].close;
    if (change >= 0) avgGain += change;
    else avgLoss -= change;
  }
  avgGain /= period;
  avgLoss /= period;
  out.push({
    time: bars[period].time,
    value: avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss),
  });
  for (let i = period + 1; i < bars.length; i++) {
    const change = bars[i].close - bars[i - 1].close;
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    const rs = avgLoss === 0 ? Infinity : avgGain / avgLoss;
    const value = Number.isFinite(rs) ? 100 - 100 / (1 + rs) : 100;
    out.push({ time: bars[i].time, value });
  }
  return out;
}

function macd(
  bars: OhlcvBar[],
  fastPeriod: number,
  slowPeriod: number,
  signalPeriod: number,
): {
  line: { time: number; value: number }[];
  signal: { time: number; value: number }[];
} {
  const fast = ema(bars, fastPeriod);
  const slow = ema(bars, slowPeriod);
  // Align: only compute MACD line where both EMAs exist.
  const slowByTime = new Map(slow.map((p) => [p.time, p.value]));
  const macdLine: { time: number; value: number }[] = [];
  for (const f of fast) {
    const s = slowByTime.get(f.time);
    if (s !== undefined) macdLine.push({ time: f.time, value: f.value - s });
  }
  const signal = emaSeries(
    macdLine.map((p) => ({ time: p.time, v: p.value })),
    signalPeriod,
  );
  return { line: macdLine, signal };
}

function stochastic(
  bars: OhlcvBar[],
  kPeriod: number,
  dPeriod: number,
): {
  k: { time: number; value: number }[];
  d: { time: number; value: number }[];
} {
  const k: { time: number; value: number }[] = [];
  if (!Number.isFinite(kPeriod) || kPeriod < 1) return { k, d: [] };
  for (let i = kPeriod - 1; i < bars.length; i++) {
    let hi = -Infinity;
    let lo = Infinity;
    for (let j = i - kPeriod + 1; j <= i; j++) {
      if (bars[j].high > hi) hi = bars[j].high;
      if (bars[j].low < lo) lo = bars[j].low;
    }
    const range = hi - lo;
    const value = range === 0 ? 50 : ((bars[i].close - lo) / range) * 100;
    k.push({ time: bars[i].time, value });
  }
  const d = emaSeries(
    k.map((p) => ({ time: p.time, v: p.value })),
    dPeriod,
  );
  return { k, d };
}

function atr(
  bars: OhlcvBar[],
  period: number,
): { time: number; value: number }[] {
  if (!Number.isFinite(period) || period < 1 || bars.length < period + 1) {
    return [];
  }
  const tr: { time: number; v: number }[] = [];
  for (let i = 1; i < bars.length; i++) {
    const prevClose = bars[i - 1].close;
    const range = Math.max(
      bars[i].high - bars[i].low,
      Math.abs(bars[i].high - prevClose),
      Math.abs(bars[i].low - prevClose),
    );
    tr.push({ time: bars[i].time, v: range });
  }
  return emaSeries(tr, period);
}

function roc(
  bars: OhlcvBar[],
  period: number,
): { time: number; value: number }[] {
  const out: { time: number; value: number }[] = [];
  if (!Number.isFinite(period) || period < 1) return out;
  for (let i = period; i < bars.length; i++) {
    const prev = bars[i - period].close;
    if (prev === 0) continue;
    out.push({
      time: bars[i].time,
      value: ((bars[i].close - prev) / prev) * 100,
    });
  }
  return out;
}

export function buildInstanceFromSpec(
  spec: IndicatorSpec,
  strategyParams: Record<string, unknown>,
  fallbackColor?: string,
): IndicatorInstance | null {
  const resolved = resolveSpec(spec, strategyParams);
  for (const v of Object.values(resolved.params)) {
    if (!Number.isFinite(v)) return null;
  }
  // A `period` that resolves to < 1 means the strategy knob is disabled
  // (e.g. trend_ma_period=0 turns off an optional trend filter). Don't seed
  // a degenerate line for it.
  if (resolved.params.period !== undefined && resolved.params.period < 1) {
    return null;
  }
  const label =
    resolved.label ?? defaultLabelFor(resolved.type, resolved.params);
  return {
    id: newIndicatorId(),
    type: resolved.type,
    params: resolved.params,
    pane: resolved.pane,
    color: resolved.color ?? fallbackColor,
    label,
  };
}

/** What the user fills in to add a new indicator from the UI. */
export function makeNewIndicatorInstance(
  type: IndicatorType,
  params: Record<string, number>,
): IndicatorInstance {
  return {
    id: newIndicatorId(),
    type,
    params,
    pane: INDICATOR_DEFAULTS[type].pane,
    label: defaultLabelFor(type, params),
  };
}

export const INDICATOR_DEFAULTS: Record<
  IndicatorType,
  { params: Record<string, number>; pane: IndicatorPane }
> = {
  sma: { params: { period: 20 }, pane: "price" },
  ema: { params: { period: 20 }, pane: "price" },
  wma: { params: { period: 20 }, pane: "price" },
  bollinger: { params: { period: 20, std_dev: 2 }, pane: "price" },
  donchian: { params: { period: 20 }, pane: "price" },
  rsi: { params: { period: 14 }, pane: "below" },
  macd: {
    params: { fast_period: 12, slow_period: 26, signal_period: 9 },
    pane: "below",
  },
  stochastic: { params: { k_period: 14, d_period: 3 }, pane: "below" },
  atr: { params: { period: 14 }, pane: "below" },
  roc: { params: { period: 10 }, pane: "below" },
  supertrend: { params: { atr_period: 10, multiplier: 3 }, pane: "price" },
  vwap: { params: {}, pane: "price" },
};
